import { tsvFieldSeparator } from "../content/csv/tsv-file.js";

/**
 * What an imported file calls its columns, where it says at all.
 *
 * Beside `import-columns.ts`, which decides which columns hold words. **The
 * name never decides a column and only labels one.** A name recognised here
 * puts a column in front of the reader already ticked, and a name absent costs
 * nothing, because the cells have already answered the question. That is what
 * makes a list this short safe, and it is why the two live apart.
 *
 * A file says what its columns are called in one of two places. A header row,
 * or an Anki `#columns:` line above the records. This site writes the first in
 * its CSV and the second in its TSV, so both are read.
 */

/** A character of Chinese, which is what tells a header row from a record. */
const han = /\p{Script=Han}/u;

/** What a preamble line declaring the column names begins with. */
const declaration = "columns:";

/**
 * The header names worth recognising, normalised the way {@link normalised}
 * normalises a header.
 *
 * `jiantihanzi` is on it because the 词典 level files use `jianti_hanzi`, and
 * `hanzi` because this site's own export uses that. `expression` and `front`
 * are Anki's own field names on the note types a Chinese deck is usually built
 * from.
 */
const headwordNames: ReadonlySet<string> = new Set([
  "hanzi",
  "jianti",
  "jiantihanzi",
  "fanti",
  "fantihanzi",
  "zhongwen",
  "chinese",
  "simplified",
  "traditional",
  "word",
  "expression",
  "front",
  "汉字",
  "漢字",
  "中文",
  "简体",
  "繁体",
  "繁體",
  "词",
  "詞",
]);

/**
 * The header names that say a column holds tags.
 *
 * Short, because a tags column is taken on its name alone and every name added
 * here is a column of free text this site will start storing. `import-tags.ts`
 * says why the name is all there is to go on and what bounds the risk.
 */
const tagNames: ReadonlySet<string> = new Set(["tags", "tag", "标签", "標籤"]);

/**
 * A header as it is compared against {@link headwordNames}.
 *
 * Lowercased, with the separators a header is written with taken out, so
 * `Jianti Hanzi`, `jianti_hanzi` and `JIANTI-HANZI` are one name. A byte order
 * mark goes too. `CsvFile` opens a file with one, and a reader told `utf-8`
 * rather than `utf-8-sig` leaves it glued to the first column's name.
 * @param name - What the file calls the column.
 * @returns The name as a key.
 */
function normalised(name: string): string {
  return name.toLowerCase().replaceAll(/[\s_\-\u{FEFF}]/gu, "");
}

/**
 * Whether a header names a column of headwords.
 *
 * Read by the review screen, which ticks the columns a reader most likely meant
 * and leaves the rest for them to tick.
 * @param name - What the file calls the column, where it says.
 * @returns Whether the name is one this site recognises.
 */
export function namesHeadwords(name: string | undefined): boolean {
  return name !== undefined && headwordNames.has(normalised(name));
}

/**
 * Whether a header names a column of tags.
 *
 * The only question about a column that the name settles on its own, every
 * other one being answered by the cells. See `import-tags.ts`.
 * @param name - What the file calls the column, where it says.
 * @returns Whether the name is one this site reads tags from.
 */
export function namesTags(name: string | undefined): boolean {
  return name !== undefined && tagNames.has(normalised(name));
}

/**
 * The column names an Anki preamble declares, where it declares any.
 *
 * `#columns:hanzi<tab>pinyin` is the line, and this site's own TSV export
 * writes one. A tab separated file carries its names there rather than in a
 * header row, since a row of names would import as a card.
 * @param comments - The lines above the first record, without their `#`.
 * @param separator - What the records were split on.
 * @returns The names in order, or nothing where the preamble declares none.
 */
export function declaredNames(
  comments: readonly string[],
  separator: string | undefined,
): readonly string[] | undefined {
  const declared = comments.find((line) =>
    line.toLowerCase().startsWith(declaration),
  );

  return declared
    ?.slice(declaration.length)
    .split(separator ?? tsvFieldSeparator);
}

/**
 * Whether the first record names the columns rather than holding a word.
 *
 * Two ways in, and the second exists because the first is wrong about a header
 * written in Chinese.
 *
 * **A record holding no Han character at all is a header.** A word list's first
 * record holds a word, and a word holds a Han character 99.97% of the time.
 * `hanzi,pinyin` is caught here.
 *
 * **A record naming one of its columns is a header too**, which `汉字,pinyin`
 * needs. Half of {@link headwordNames} is written in Chinese, so a rule about
 * Han characters alone reads those headers as words and imports 汉字 as one.
 *
 * That second way asks for more than one cell, and the asymmetry is deliberate.
 * 中文, 汉字 and 词 are all headwords the 词典 publishes, so a one-column list
 * beginning with one of them would lose its first word to a rule that took the
 * name on its own. Keeping it costs an extra candidate the reader can untick on
 * the review screen. Dropping it would cost a word they meant to import and
 * would show only as an absence.
 *
 * The file this still reads wrong is a list whose first entry is one of the 57
 * headwords the section publishes with no Han character in it, `110` and `〇`
 * among them. That costs the reader one row and the review screen shows it.
 * @param rows - The records as they were read.
 * @returns Whether to take the first record as names.
 */
export function headsColumns(rows: readonly (readonly string[])[]): boolean {
  const first = rows[0];

  if (rows.length < 2 || first === undefined) {
    return false;
  }

  return (
    !first.some((cell) => han.test(cell)) ||
    (first.length > 1 && first.some((cell) => namesHeadwords(cell)))
  );
}
